const DOMManipulator = (() => {
    function DOMManipulator() {
        /**
        * Create a DOM element with various attributes
        *
        * @param {String} tagname
        * @param {Object} attrs
        * @return {Element}
        */
        this.createElement = function(tagname, attrs) {
            let element = document.createElement(tagname);

            attrs = attrs || {};

            const setAttRegex = /^(?:role|data-|aria-)/;

            for (const k in attrs) {
                let v = attrs[k];

                if (k === "style") {
                    this.setStyle(element, v);
                } else if (k.match(setAttRegex)) {
                    element.setAttribute(k, v);
                } else {
                    element[k] = v;
                }
            }

            return element;
        }

        /**
        * Removes `element` from `parentElement`
        *
        * @param {Element} element
        * @param Boolean [animate=false]
        */
        this.removeElement = function(element, animate) {
            if (!element || !element.parentElement) return;

            const parentElement = element.parentElement;

            if (animate) {
                this.setStyle(element, {
                    opacity: "0",
                });

                window.setTimeout(() => {
                    try {
                        parentElement.removeChild(element);
                    } catch (e) {}
                }, 500);
            } else {
                parentElement.removeChild(element);
            }
        }

        /**
        * Sets the style of an DOM element
        *
        * @param {Element} element
        * @param {Object|string} style
        * @return void
        */
        this.setStyle = function(element, style) {
            let cssText = "";

            if (element.style.cssText) {
                cssText += element.style.cssText;
            }

            if (typeof style === "string") {
                cssText += style;
            } else {
                for (const rule in style) {
                    cssText += `${rule}:${style[rule]};`;
                }
            }

            element.style.cssText = cssText;
        }

        /**
        * Append a class to an element
        *
        * @param {Element} element
        * @param {String} className
        * @returns {void}
        */
        this.addClass = function(element, className) {
            if (!element) { return; }

            if (element instanceof SVGElement) {
                const svgClass = element.getAttribute("class") || "";

                if (!svgClass.match(className)) {
                    element.setAttribute("class", `${svgClass} ${className}`);
                }
            } else {
                if (element.classList !== undefined) {
                    const classes = className.split(" ");
                    lreForEach(classes, (classname) => {
                        element.classList.add(classname);
                    });
                } else if (!element.className.match(className)) {
                    element.className += ` ${className}`;
                }
            }
        }

        /**
        * Remove a class from an element
        *
        * @param {Element} element
        * @param {RegExp|String} classNameRegex can be regex or string
        * @returns {void}
        */
        this.removeClass = function(element, classNameRegex) {
            if (!element) { return; }

            if (element instanceof SVGElement) {
                const svgClass = element.getAttribute("class") || "";
                element.setAttribute( "class", svgClass.replace(classNameRegex, "").replace(/^\s+|\s+$/g, "") );
            } else {
                element.className = element.className.replace(classNameRegex, "").replace(/^\s+|\s+$/g, "");
            }
        }

        /**
        * Appends `element` to `parentElement`
        *
        * @param {Element} parentElement
        * @param {Element} element
        * @param {Boolean} [animate=false]
        */
        this.appendChild = function(parentElement, element, animate) {
            if (animate) {
                const elementCurrentOpacity = element.style.opacity || "1";

                this.setStyle(element, {
                    opacity: "0",
                });

                window.setTimeout(() => {
                    this.setStyle(element, {
                        opacity: elementCurrentOpacity,
                    });
                }, 10);
            }

            parentElement.appendChild(element);
        }

        /**
        * Appends multiple `elements` to `parentElement`
        *
        * @param {Element} parentElement
        * @param {HTMLCollection|Array} elements
        * @param {Boolean} [animate=false]
        */
        this.append = function(parentElement, elements, animate) {
            izzyForEach(elements, (element) => {
                this.appendChild(parentElement, element, animate);
            });
        }

        /**
        * Prepend `element` to `parentElement`
        *
        * @param {Element} parentElement
        * @param {Element} element
        * @param {Boolean} [animate=false]
        */
        this.prepend = function(parentElement, element, animate) {
            if (animate) {
                const elementCurrentOpacity = element.style.opacity || "1";

                this.setStyle(element, {
                    opacity: "0",
                });

                window.setTimeout(() => {
                    this.setStyle(element, {
                        opacity: elementCurrentOpacity,
                    });
                }, 10);
            }

            parentElement.prepend(element);
        }

        /**
        * Appends multiple `elements` to `parentElement`
        *
        * @param {Element} parentElement
        * @param {HTMLCollection|Array} elements
        * @param {Boolean} [animate=false]
        */
        this.appendChildren = function(parentElement, elements, animate) {
            this.append(parentElement, elements, animate);
        }

        /**
        * Check if parentELement has children
        *
        * @param {Element} parentElement
        * @returns {Boolean}
        */
        this.hasChildren = function(parentElement) {
            return parentElement.hasChildNodes();
        }

        /**
        * Check if and element `parentElement` contains a specific child node
        *
        * @param {Element} parentELement
        * @param {Element} childNode
        * @returns {Boolean}
        */
        this.containsChildNode = function(parentELement, childNode) {
            return parentELement.contains(childNode)
        }

        /**
        * Check if an element `parentElement` has a child with a particular class name
        *
        * @param {String} className
        * @param {Element} parentElement
        * @returns {Boolean}
        */
        this.hasChildWithClassName = function(className, parentElement) {
            return parentElement.querySelector(`.${className}`) != null ? true : false;
        }

        /**
        * Check if an element `parentElement` has a child with a particular id
        *
        * @param {String} id
        * @param {Element} parentElement
        * @returns {Boolean}
        */
        this.hasChildWithId = function(id, parentElement) {
            return parentElement.querySelector(`#${id}`) != null ? true : false;
        },

        /**
        * Clear all children of an element `parentElement`
        *
        * @param {Element} parentElement
        * @returns {void}
        */
        this.clearChildren = function(parentElement) {
            while (parentElement.firstChild) {
                parentElement.firstChild.remove();
            }
        }

        /**
        * Set CSS Property for/on an element
        *
        * @param {Element} element
        * @param {Object} cssProps
        */
        this.setCssProperties = function(element, cssProps = {}) {
            for (const prop in cssProps) {
                if (Object.hasOwnProperty.call(cssProps, prop)) {
                    const propVal = cssProps[prop];
                    element.style.setProperty(prop, propVal);
                }
            }
        }

        /**
        * Set CSS Property for/on an element
        *
        * @param {Element} element
        * @param {Object} cssProps
        */
        this.removeCssProperties = function(element, cssProps = []) {
            cssProps.forEach(prop => {
                element.style.removeProperty(prop);
            });
        }

        /**
        * Insert a DOM element before another element
        *
        * @param {Element} element
        * @param {Element} beforeElement
        */
        this.insertBefore = function(element, beforeElement) {
            beforeElement.parentNode.insertBefore(element, beforeElement);
        }

        /**
        * Insert a DOM element after another element
        *
        * @param {Element} element
        * @param {Element} afterElement
        */
        this.insertAfter = function(element, afterElement) {
            afterElement.parentNode.insertBefore(element, afterElement.nextSibling);
        }

        this.getChildrenByClassName = function(parent, className) {
            return parent.querySelectorAll(`.${className}`);
        }
    }

    return new DOMManipulator();
})();

export default DOMManipulator;